import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Palette, CloudLightning, RefreshCw, Plus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@/components/ui/progress';
import { callSiteApi, generateHMACSignature } from '../utils';
import type { Site, Plugin, Theme, CoreInfo, UptimeEvent, PerfScores, BulkActionProgress } from '../types';
import { PluginsTab } from './PluginsTab';
import { ThemesTab } from './ThemesTab';
import { WpUpdatesTab } from './WpUpdatesTab';
import { UptimeMonitorWidget } from './UptimeMonitorWidget';
import { BackupsWidget } from './BackupsWidget';
import { DiagnosticsPanel } from './DiagnosticsPanel';
import { ChangelogModal } from './ChangelogModal';
import { InstallModal } from './InstallModal';
import { StatsRow } from './StatsRow';

interface SiteOverviewProps {
  site: Site;
  onUpdateSite?: (updates: Partial<Site>) => void;
  uptimeEvents: UptimeEvent[];
}

export const SiteOverview: React.FC<SiteOverviewProps> = ({ site, onUpdateSite, uptimeEvents }) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'plugins' | 'themes' | 'wp-updates'>('plugins');

  const [plugins, setPlugins] = useState<Plugin[]>(site.last_plugins_data || []);
  const [themes, setThemes] = useState<Theme[]>(site.last_themes_data || []);
  const [coreInfo, setCoreInfo] = useState<CoreInfo | null>(site.last_core_info || null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedPlugins, setSelectedPlugins] = useState<string[]>([]);
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [errorCore, setErrorCore] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [changelogData, setChangelogData] = useState<any>(null);
  const [changelogLoading, setChangelogLoading] = useState(false);
  const [changelogTitle, setChangelogTitle] = useState<string | null>(null);
  const [bulkActionProgress, setBulkActionProgress] = useState<BulkActionProgress | null>(null);

  const [savingInterval, setSavingInterval] = useState(false);

  const [showInstall, setShowInstall] = useState(false);
  const [installTab, setInstallTab] = useState<'search' | 'zip'>('search');
  const [installSearchQuery, setInstallSearchQuery] = useState('');
  const [installResults, setInstallResults] = useState<any[]>([]);
  const [installSearching, setInstallSearching] = useState(false);
  const [installingSlug, setInstallingSlug] = useState<string | null>(null);
  const [installMessage, setInstallMessage] = useState<string | null>(null);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [zipUploading, setZipUploading] = useState(false);
  const [installPage, setInstallPage] = useState(1);
  const [hasMoreInstall, setHasMoreInstall] = useState(true);
  const [installType, setInstallType] = useState<'plugin' | 'theme'>('plugin');

  const [perfDevice, setPerfDevice] = useState<'mobile' | 'desktop'>('mobile');
  const [perfData, setPerfData] = useState<{ mobile: PerfScores | null; desktop: PerfScores | null }>({
    mobile: site.last_perf_mobile || null,
    desktop: site.last_perf_desktop || null,
  });
  const [loadingPerf, setLoadingPerf] = useState(false);

  useEffect(() => {
    setPerfData({
      mobile: site.last_perf_mobile || null,
      desktop: site.last_perf_desktop || null,
    });
  }, [site.last_perf_mobile, site.last_perf_desktop]);

  const callApi = async (route: string, method = 'GET', body?: string) => {
    return callSiteApi(site.url, site.publicKey, site.secretKey, route, method, body);
  };

  const syncPlugins = async () => {
    if (!site) return;
    try {
      const res = await callApi('plugins');
      const data = res.plugins ?? [];
      setPlugins(data);
      await supabase.from('workspace_sites').update({ last_plugins_data: data }).eq('id', site.id);
    } catch (e: any) {
      console.error('Failed to sync plugins:', e.message);
    }
  };

  const syncThemes = async () => {
    if (!site) return;
    try {
      const res = await callApi('themes');
      const data = res.themes ?? [];
      setThemes(data);
      await supabase.from('workspace_sites').update({ last_themes_data: data }).eq('id', site.id);
    } catch (e: any) {
      console.error('Failed to sync themes:', e.message);
    }
  };

  const handleSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    setErrorCore(null);

    try {
      const [pRes, tRes, cRes] = await Promise.all([
        callApi('plugins'),
        callApi('themes'),
        callApi('core/info'),
      ]);

      const newPlugins = pRes.plugins ?? [];
      const newThemes = tRes.themes ?? [];
      const newCore = cRes;

      setPlugins(newPlugins);
      setThemes(newThemes);
      setCoreInfo(newCore);

      const { error } = await supabase
        .from('workspace_sites')
        .update({
          last_plugins_data: newPlugins,
          last_themes_data: newThemes,
          last_core_info: newCore,
          last_sync_at: new Date().toISOString(),
        })
        .eq('id', site.id);

      if (error) throw error;
    } catch (e: any) {
      console.error('Sync failed:', e);
    } finally {
      setIsSyncing(false);
    }
  };

  const handlePluginAction = async (action: string, pluginFile: string) => {
    try {
      setActionLoading(`plugin-${pluginFile}-${action}`);
      const body = JSON.stringify({ plugin_file: pluginFile });
      await callApi(`plugins/${action}`, 'POST', body);
      await syncPlugins();
      toast({ title: 'Action Success', description: `Successfully performed ${action} on plugin.` });
    } catch (e: any) {
      toast({ title: 'Action Failed', description: e.message || `Could not ${action} plugin.`, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleThemeAction = async (action: string, stylesheet: string) => {
    try {
      setActionLoading(`theme-${stylesheet}-${action}`);
      const body = JSON.stringify({ stylesheet });
      await callApi(`themes/${action}`, 'POST', body);
      await syncThemes();
      toast({ title: 'Action Success', description: `Successfully performed ${action} on theme.` });
    } catch (e: any) {
      toast({ title: 'Action Failed', description: e.message || `Could not ${action} theme.`, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const runBulkAction = async (items: string[], actionFn: (item: string) => Promise<void>, label: string) => {
    setBulkActionProgress({ current: 0, total: items.length, label });
    for (let i = 0; i < items.length; i++) {
      setBulkActionProgress({ current: i + 1, total: items.length, label });
      await actionFn(items[i]);
    }
    setBulkActionProgress(null);
  };

  const handleOpenChangelog = async (type: string, itemFileOrSlug: string, name: string) => {
    try {
      setChangelogTitle(name);
      setChangelogLoading(true);
      setChangelogData(null);
      const res = await callApi(`${type}s/changelog`, 'POST', JSON.stringify({
        [type === 'plugin' ? 'plugin_file' : 'stylesheet']: itemFileOrSlug,
      }));
      setChangelogData(res.changelog || 'No changelog found.');
    } catch {
      setChangelogData('Failed to load changelog.');
    } finally {
      setChangelogLoading(false);
    }
  };

  const handleOpenInstall = (type: 'plugin' | 'theme') => {
    setInstallType(type);
    setShowInstall(true);
    setInstallTab('search');
    setInstallSearchQuery('');
    setInstallPage(1);
    setInstallMessage(null);
    setZipFile(null);

    if (site) {
      const cached = type === 'plugin' ? site.repo_popular_plugins : site.repo_popular_themes;
      if (cached && cached.length > 0) {
        setInstallResults(cached);
        setHasMoreInstall(true);
      } else {
        handleInstallSearch('', 1, type);
      }
    } else {
      handleInstallSearch('', 1, type);
    }
  };

  const handleInstallSearch = async (q: string, page = 1, typeOverride?: 'plugin' | 'theme') => {
    const activeType = typeOverride ?? installType;
    setInstallSearchQuery(q);
    setInstallPage(page);
    setInstallSearching(true);
    try {
      const res = await callApi(`${activeType}s/search?query=${encodeURIComponent(q)}&page=${page}`);
      const items = res.plugins ?? res.themes ?? [];

      if (page === 1) {
        setInstallResults(items);
        if (!q && site) {
          const updateObj = activeType === 'plugin'
            ? { repo_popular_plugins: items, repo_cache_at: new Date().toISOString() }
            : { repo_popular_themes: items, repo_cache_at: new Date().toISOString() };
          await supabase.from('workspace_sites').update(updateObj).eq('id', site.id);
        }
      } else {
        setInstallResults(prev => [...prev, ...items]);
      }

      setHasMoreInstall(items.length >= 12);
    } catch {
      if (page === 1) setInstallResults([]);
    } finally {
      setInstallSearching(false);
    }
  };

  const handleInstallFromRepo = async (slug: string) => {
    setInstallingSlug(slug);
    setInstallMessage(null);
    try {
      const res = await callApi(`${installType}s/install`, 'POST', JSON.stringify({ slug }));
      setInstallMessage(res.message || 'Installed successfully!');
      if (installType === 'plugin') await syncPlugins();
      else await syncThemes();
    } catch (e: any) {
      setInstallMessage(`Error: ${e.message}`);
    } finally {
      setInstallingSlug(null);
    }
  };

  const handleInstallZip = async () => {
    if (!zipFile) return;
    setZipUploading(true);
    setInstallMessage(null);
    try {
      const formData = new FormData();
      formData.append('zip_file', zipFile);
      const formattedUrl = site.url.replace(/\/+$/, '');
      const route = `${installType}s/install-zip`;
      const endpoint = `${formattedUrl}/wp-json/agencyplus/v1/${route}`;
      const timestamp = Math.floor(Date.now() / 1000).toString();
      const canonicalString = `${timestamp}\nPOST\n/agencyplus/v1/${route}\n`;
      const signature = await generateHMACSignature(site.secretKey, canonicalString);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'X-AgencyPlus-Public-Key': site.publicKey,
          'X-AgencyPlus-Signature': signature,
          'X-AgencyPlus-Timestamp': timestamp,
        },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || `HTTP ${response.status}`);
      setInstallMessage(data.message || 'Installed successfully!');
      if (installType === 'plugin') await syncPlugins();
      else await syncThemes();
    } catch (e: any) {
      setInstallMessage(`Error: ${e.message}`);
    } finally {
      setZipUploading(false);
    }
  };

  const handleCoreUpdate = async () => {
    if (!window.confirm('Update WordPress to the latest version?')) return;
    try {
      setActionLoading('core-update');
      await callApi('core/update', 'POST');
      await handleSync();
    } catch (e: any) {
      alert(`Update failed: ${e.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const updateUptimeInterval = async (interval: number) => {
    try {
      setSavingInterval(true);
      const { error } = await supabase
        .from('workspace_sites')
        .update({ uptime_interval: interval })
        .eq('id', site.id);
      if (error) throw error;
      if (onUpdateSite) onUpdateSite({ uptime_interval: interval });
    } catch (err: any) {
      alert('Failed to update frequency: ' + err.message);
    } finally {
      setSavingInterval(false);
    }
  };

  const toggleUptimeMonitor = async () => {
    const newState = !site.uptime_enabled;
    const updateData: any = { uptime_enabled: newState };
    if (newState) updateData.uptime_status = 'unknown';

    if (onUpdateSite) onUpdateSite(updateData);

    try {
      const { error } = await supabase
        .from('workspace_sites')
        .update(updateData)
        .eq('id', site.id);
      if (error) throw error;
    } catch (err: any) {
      if (onUpdateSite) onUpdateSite({ uptime_enabled: !newState, uptime_status: site.uptime_status });
      alert('Failed to toggle monitor: ' + err.message);
    }
  };

  const fetchPageSpeed = async (device: 'mobile' | 'desktop' | 'both', force = false) => {
    if (loadingPerf) return;
    if (!force && device !== 'both' && perfData[device as 'mobile' | 'desktop']) return;
    if (!force && device === 'both' && perfData.mobile && perfData.desktop) return;

    const isLocal = site.url.includes('.local') || site.url.includes('localhost') || site.url.includes('127.0.0.1');
    if (isLocal) {
      alert('Google PageSpeed Insights cannot analyze local URLs. Please use a public site address.');
      return;
    }

    setLoadingPerf(true);
    try {
      const devicesToFetch: ('mobile' | 'desktop')[] = device === 'both' ? ['mobile', 'desktop'] : [device as 'mobile' | 'desktop'];

      const fetchResults = await Promise.all(devicesToFetch.map(async (dev) => {
        const { data, error } = await supabase.functions.invoke('pagespeed-proxy', {
          body: { url: site.url, strategy: dev },
        });
        if (error) throw error;
        if (data.error) throw new Error(data.error);

        const lighthouse = data.lighthouseResult;
        const categories = lighthouse.categories;

        const scores: PerfScores = {
          perf: Math.round((categories.performance?.score || 0) * 100),
          acc: Math.round((categories.accessibility?.score || 0) * 100),
          best: Math.round((categories['best-practices']?.score || 0) * 100),
          seo: Math.round((categories.seo?.score || 0) * 100),
          fetchedAt: new Date().toISOString(),
        };

        return { device: dev, scores };
      }));

      const finalPerfData = { ...perfData };
      const dbUpdates: any = {};

      fetchResults.forEach(res => {
        finalPerfData[res.device] = res.scores;
        const column = res.device === 'mobile' ? 'last_perf_mobile' : 'last_perf_desktop';
        dbUpdates[column] = res.scores;
      });

      setPerfData(finalPerfData);
      if (onUpdateSite) onUpdateSite(dbUpdates);
      await supabase.from('workspace_sites').update(dbUpdates).eq('id', site.id);
    } catch (err: any) {
      console.error('PageSpeed fetch error:', err);
      let msg = err.message;
      if (msg.includes('Quota exceeded')) msg = 'PageSpeed API quota exceeded. Please check your Supabase secrets.';
      if (msg.includes('PAGESPEED_API_KEY')) msg = 'PageSpeed API Key not configured in Supabase secrets.';
      alert('Failed to fetch diagnostics: ' + msg);
    } finally {
      setLoadingPerf(false);
    }
  };

  useEffect(() => {
    if (plugins.length === 0 && themes.length === 0 && !coreInfo) {
      handleSync();
    }
  }, [site.id]);

  useEffect(() => {
    if (site?.id && !site.last_perf_mobile && !site.last_perf_desktop) {
      fetchPageSpeed('both');
    }
  }, [site?.id]);

  const coreHasUpdate = coreInfo?.wordpress?.has_update ?? false;

  const overviewTabs = [
    { id: 'plugins' as const, label: 'Plugins', icon: Package, count: plugins.length },
    { id: 'themes' as const, label: 'Themes', icon: Palette, count: themes.length },
    { id: 'wp-updates' as const, label: 'WP Updates', icon: CloudLightning, count: coreHasUpdate ? 1 : 0 },
  ];

  return (
    <>
      <div className="space-y-4">
        <StatsRow site={site} coreInfo={coreInfo} isSyncing={isSyncing} />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-stretch w-full">
          {/* Top Row: Diagnostics (Left Wide) & Uptime (Right Narrow) */}
          <div className="md:col-span-8 xl:col-span-9 flex">
            <DiagnosticsPanel
              siteUrl={site.url}
              perfDevice={perfDevice}
              onSetPerfDevice={setPerfDevice}
              perfData={perfData}
              loadingPerf={loadingPerf}
              onRefresh={() => fetchPageSpeed('both', true)}
            />
          </div>
          <div className="md:col-span-4 xl:col-span-3 flex">
            <UptimeMonitorWidget
              site={site}
              uptimeEvents={uptimeEvents}
              onToggleUptime={toggleUptimeMonitor}
              onUpdateInterval={updateUptimeInterval}
              savingInterval={savingInterval}
            />
          </div>

          {/* Bottom Row: Update Manager (Tabs) */}
          <div className="md:col-span-8 xl:col-span-9 bg-card border border-border rounded-xl overflow-hidden relative min-w-0">
            <div className="flex border-b border-border bg-muted/30">
              <div className="flex flex-1 overflow-x-auto scrollbar-none">
                {overviewTabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all relative ${isActive ? 'text-foreground bg-card' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                      {tab.count > 0 && (
                        <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-workspace-accent/10 text-workspace-accent' : 'bg-muted text-muted-foreground'}`}>
                          {tab.count}
                        </span>
                      )}
                      {isActive && (
                        <motion.div layoutId="overview-tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-workspace-accent" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center px-4 gap-2 border-l border-border bg-card/50">
                <button
                  onClick={handleSync}
                  disabled={isSyncing}
                  className={`p-2 rounded-lg transition-all flex items-center gap-2 group ${isSyncing ? 'text-muted-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                  title="Refresh Data"
                >
                  <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
                  <span className="text-xs font-medium hidden md:inline">
                    {isSyncing ? 'Syncing...' : 'Reload Data'}
                  </span>
                </button>

                {(activeTab === 'plugins' || activeTab === 'themes') && (
                  <button
                    onClick={() => handleOpenInstall(activeTab === 'plugins' ? 'plugin' : 'theme')}
                    className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-workspace-accent hover:bg-workspace-accent-hover text-white text-xs font-semibold transition-all shadow-sm"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Install
                  </button>
                )}
              </div>

              {bulkActionProgress && (
                <div className="absolute inset-x-0 bottom-0 z-50 bg-background/80 backdrop-blur-sm border-t border-border flex flex-col px-5 py-3 gap-2">
                  <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.1em]">
                    <span className="text-workspace-accent">{bulkActionProgress.label}</span>
                    <span className="text-muted-foreground">{bulkActionProgress.current} / {bulkActionProgress.total}</span>
                  </div>
                  <Progress value={(bulkActionProgress.current / bulkActionProgress.total) * 100} className="h-1 bg-muted rounded-full" />
                </div>
              )}
            </div>

            <div>
              <AnimatePresence mode="wait">
                {activeTab === 'plugins' && (
                  <motion.div key="plugins" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <PluginsTab
                      plugins={plugins}
                      selectedPlugins={selectedPlugins}
                      onToggleSelection={(file) => setSelectedPlugins(prev => prev.includes(file) ? prev.filter(f => f !== file) : [...prev, file])}
                      onSelectAll={() => setSelectedPlugins(plugins.map(p => p.plugin_file))}
                      onDeselectAll={() => setSelectedPlugins([])}
                      actionLoading={actionLoading}
                      bulkActionProgress={bulkActionProgress}
                      onPluginAction={handlePluginAction}
                      onBulkAction={runBulkAction}
                      onOpenChangelog={handleOpenChangelog}
                      isSyncing={isSyncing}
                    />
                  </motion.div>
                )}

                {activeTab === 'themes' && (
                  <motion.div key="themes" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <ThemesTab
                      themes={themes}
                      selectedThemes={selectedThemes}
                      onToggleSelection={(s) => setSelectedThemes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                      onSelectAll={() => setSelectedThemes(themes.map(t => t.stylesheet))}
                      onDeselectAll={() => setSelectedThemes([])}
                      actionLoading={actionLoading}
                      bulkActionProgress={bulkActionProgress}
                      onThemeAction={handleThemeAction}
                      onBulkAction={runBulkAction}
                      onOpenChangelog={handleOpenChangelog}
                      isSyncing={isSyncing}
                    />
                  </motion.div>
                )}

                {activeTab === 'wp-updates' && (
                  <motion.div key="wp-updates" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <WpUpdatesTab
                      coreInfo={coreInfo}
                      coreHasUpdate={coreHasUpdate}
                      isSyncing={isSyncing}
                      errorCore={errorCore}
                      actionLoading={actionLoading}
                      onCoreUpdate={handleCoreUpdate}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="md:col-span-4 xl:col-span-3 flex">
            <BackupsWidget site={site} />
          </div>
        </div>
      </div>

      {changelogTitle && (
        <ChangelogModal
          title={changelogTitle}
          data={changelogData}
          loading={changelogLoading}
          onClose={() => setChangelogTitle(null)}
        />
      )}

      <InstallModal
        show={showInstall}
        installType={installType}
        installTab={installTab}
        onSetInstallTab={setInstallTab}
        installSearchQuery={installSearchQuery}
        onSetInstallSearchQuery={setInstallSearchQuery}
        installResults={installResults}
        installSearching={installSearching}
        installingSlug={installingSlug}
        installMessage={installMessage}
        installPage={installPage}
        hasMoreInstall={hasMoreInstall}
        zipFile={zipFile}
        zipUploading={zipUploading}
        plugins={plugins}
        themes={themes}
        onClose={() => setShowInstall(false)}
        onSearch={handleInstallSearch}
        onInstallFromRepo={handleInstallFromRepo}
        onInstallZip={handleInstallZip}
        onSetZipFile={setZipFile}
      />
    </>
  );
};
